"use client";

import { Button, ButtonProps } from "@decibeltrade/web-core/components";
import { useTranslations } from "next-intl";
import { css, cx } from "styled-system/css";

import { openKeylessWindow } from "@/utils/keyless/window";

import { MediaQuery } from "../media-query";
import { Tooltip } from "../tooltip";
import { AppleLogo } from "../vectors/apple-logo";
import { GoogleLogo } from "../vectors/google-logo";

interface KeylessButtonProps {
  sameWindow?: boolean;
  provider: "google" | "apple";
  disabled?: boolean;
  className?: string;
}

export const getKeylessProviderIcon = (provider: "google" | "apple") => {
  return provider === "google" ? GoogleLogo : AppleLogo;
};

export const KeylessButtonLayout = ({
  shortLabel,
  label,
  className,
  ...props
}: Omit<ButtonProps, "children"> & { shortLabel: string; label: string; className?: string }) => {
  const t = useTranslations("walletSelector");
  return (
    <>
      <MediaQuery display="below" breakpoint="sm">
        {(mediaClassName) => (
          // TODO: Remove tooltip once both keyless options are enabled in all environments
          <Tooltip content={t("keylessButtonTooltip")} disabled={!props.disabled}>
            <Button
              className={cx(className, mediaClassName, css({ h: "[34px!]" }))}
              variant="secondary"
              fullWidth
              {...props}
            >
              {shortLabel}
            </Button>
          </Tooltip>
        )}
      </MediaQuery>
      <MediaQuery display="above" breakpoint="sm">
        {(mediaClassName) => (
          // TODO: Remove tooltip once both keyless options are enabled in all environments
          <Tooltip content={t("keylessButtonTooltip")} disabled={!props.disabled}>
            <Button
              className={cx(className, mediaClassName)}
              variant="secondary"
              fullWidth
              {...props}
            >
              {label}
            </Button>
          </Tooltip>
        )}
      </MediaQuery>
    </>
  );
};

export function KeylessButton({ sameWindow, provider, disabled, className }: KeylessButtonProps) {
  const t = useTranslations("authWindow");

  const label = provider === "google" ? t("continueWithGoogle") : t("continueWithApple");
  const shortLabel = provider === "google" ? t("google") : t("apple");
  const Icon = getKeylessProviderIcon(provider);

  return (
    <KeylessButtonLayout
      LeadIcon={Icon}
      onClick={() => openKeylessWindow(provider, sameWindow)}
      disabled={disabled}
      ariaLabel={label}
      shortLabel={shortLabel}
      label={label}
      className={className}
    />
  );
}
